import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Snackbar,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { createOrganization } from '../utils/organizationsApi';

const OrganizationsPage = () => {
  const { organizations, loading, error, refreshOrganizations, setCurrentOrganization } = useWorkspace();
  const navigate = useNavigate();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgDomain, setNewOrgDomain] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    refreshOrganizations();
  }, [refreshOrganizations]);

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim()) {
      setCreateError('Organization name is required');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      const newOrg = await createOrganization({
        name: newOrgName.trim(),
        domain: newOrgDomain.trim() || undefined,
      });

      setSuccessMessage(`Organization "${newOrg.name}" created successfully!`);
      setCreateDialogOpen(false);
      setNewOrgName('');
      setNewOrgDomain('');
      await refreshOrganizations();
    } catch (err) {
      console.error('Error creating organization:', err);
      setCreateError(err.response?.data?.message || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenOrganization = (org) => {
    setCurrentOrganization(org);
    navigate(`/organizations/${org.id}`);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const renderSkeletons = () => (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: 2,
    }}>
      {[...Array(3)].map((_, index) => (
        <Card key={index}>
          <CardContent>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="30%" height={20} />
          </CardContent>
          <CardActions>
            <Skeleton variant="rectangular" width={100} height={30} />
          </CardActions>
        </Card>
      ))}
    </Box>
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Organizations
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Organization
          </Button>
        </Box>

        <Typography variant="body1" paragraph color="text.secondary">
          Manage your organizations and teams. Create workspaces to organize your planning poker sessions.
        </Typography>

        {loading ? (
          renderSkeletons()
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : organizations.length > 0 ? (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 2,
          }}>
            {organizations.map((org) => (
              <Card
                key={org.id}
                elevation={2}
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8,
                  },
                }}
                onClick={() => handleOpenOrganization(org)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {org.logo ? (
                      <Box
                        component="img"
                        src={org.logo}
                        alt={org.name}
                        sx={{ width: 40, height: 40, borderRadius: 1, mr: 2 }}
                      />
                    ) : (
                      <BusinessIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                    )}
                    <Box>
                      <Typography variant="h6">{org.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {org.slug}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <GroupIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {org.teams?.length || 0} teams
                    </Typography>
                    <Box
                      sx={{
                        ml: 'auto',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: `${getRoleBadgeColor(org.role)}.main`,
                        color: 'white',
                        fontSize: '0.75rem',
                        textTransform: 'capitalize',
                      }}
                    >
                      {org.role}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No organizations yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first organization to start collaborating with your team.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Your First Organization
            </Button>
          </Box>
        )}
      </Paper>

      {/* Create Organization Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a new organization to manage your teams and sessions.
          </DialogContentText>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            fullWidth
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            required
          />
          <TextField
            margin="dense"
            label="Domain (optional)"
            fullWidth
            value={newOrgDomain}
            onChange={(e) => setNewOrgDomain(e.target.value.toLowerCase())}
            helperText="e.g., company.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateOrganization}
            variant="contained"
            disabled={creating || !newOrgName.trim()}
          >
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationsPage;
